'use client'

import React from 'react'
import Image from 'next/image'
import GradientSvgIcon from '@/components/GradientSvgIcon'
import { usesInsetProductImage } from '@/components/Refresh/SearchResults/searchResultsUtils'
import type { ServiceItem } from '@/types/interfaces/service-item'
import classes from './ServiceImage.module.css'

const ASSET_BASE_URL = `https://${process.env.NEXT_PUBLIC_ASSETS_DOMAIN}`

interface ServiceImageProps {
    service: ServiceItem
}

function getImageAssetUrl(url: string) {
    return `${ASSET_BASE_URL}/${url}`
}

/**
 * Featured-category image well (136×87).
 * Visits: full-bleed cover. Lab/med: centered product (no search cream fill).
 */
export default function ServiceImage({ service }: ServiceImageProps) {
    const url = String(service.imageUrl ?? '')
    const isFallbackIcon = url.startsWith('fallback-icons/')
    const isPlaceholder = url.startsWith('fallback_images/')
    const isLab = String(service.category ?? '').toLowerCase() === 'lab'
    const usesInsetImage = usesInsetProductImage(service.category) || isPlaceholder || isFallbackIcon

    const wrapperClassName = [
        classes.wrapper,
        usesInsetImage ? classes.wrapperInset : classes.wrapperCover,
    ].join(' ')

    if (!url) {
        return <div className={wrapperClassName} aria-hidden />
    }

    if (isFallbackIcon) {
        return (
            <div className={wrapperClassName} aria-hidden>
                <GradientSvgIcon src={getImageAssetUrl(url)} size={72} />
            </div>
        )
    }

    if (usesInsetImage) {
        const insetSize = isLab ? 102 : 86
        return (
            <div className={wrapperClassName}>
                <Image
                    src={getImageAssetUrl(url)}
                    alt=""
                    width={insetSize}
                    height={insetSize}
                    sizes={`${insetSize}px`}
                    draggable={false}
                    className={isLab ? classes.imageInsetLab : classes.imageInsetMed}
                    loading="lazy"
                    decoding="async"
                />
            </div>
        )
    }

    return (
        <div className={wrapperClassName}>
            <Image
                src={getImageAssetUrl(url)}
                alt=""
                fill
                sizes="136px"
                draggable={false}
                className={classes.imageCover}
                loading="lazy"
                decoding="async"
            />
        </div>
    )
}
